import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import { useConfirmSignInMutation } from 'api/main';
import { validateSchema } from 'utils/helpers';
import { CognitoErrors } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent } from '@mui/material';

const schema = Joi.string().trim().length(6).pattern(/^\d+$/).required().messages({
    'string.empty': texts.CODE_REQUIRED,
    'string.length': texts.LE_CODE_N_EST_PAS_VALIDE,
    'string.pattern.base': texts.LE_CODE_N_EST_PAS_VALIDE,
});

const ConfirmSignInDialog = ({ open, onClose, onSuccess }) => {
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [confirmSignIn, { isLoading: confirmLoader, error, isSuccess }] =
        useConfirmSignInMutation();

    const handleChange = useCallback(e => {
        setCode(e.target.value);
    }, []);

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, code);
        if (valid) {
            setCodeError('');
            confirmSignIn({ code });
        } else {
            setCodeError(newErrors['']);
        }
    }, [code, confirmSignIn]);

    useEffect(() => {
        if (error?.code === CognitoErrors.CODE_MISMATCH) {
            setCodeError(texts.LE_CODE_N_EST_PAS_VALIDE);
        } else if (error?.code === CognitoErrors.CODE_EXPIRED) {
            setCodeError(texts.CODE_EXPIRED_RETRY);
        }
    }, [error]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess();
        }
    }, [isSuccess, onSuccess]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle
                title={texts.TWO_FACTOR_AUTHENTICATION_TITLE}
                handleClose={onClose}
            />
            <DialogContent
                onKeyUp={e => {
                    if (e.key === 'Enter') {
                        handleSubmit();
                    }
                }}
            >
                <CustomTextField
                    inputRef={input => input && input.focus()}
                    label={texts.ENTER_CODE_FROM_SMS}
                    value={code}
                    onChange={handleChange}
                    error={codeError}
                />
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>{texts.CANCEL}</CancelButton>
                <ActionButton loading={confirmLoader} onClick={handleSubmit}>
                    {texts.CONTINUER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

ConfirmSignInDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default ConfirmSignInDialog;
